import { useRef, useState } from "react";
import { RubberStamp } from "@/components/primitives";

interface PostCarouselProps {
  photos: string[];
  alt: string;
  verdict: string;
  conf: number;
  soldAt?: string | null;
  soldPrice?: number | null;
  soldOn?: string | null;
  onClick?: () => void;
  /** When true, suppresses the VINTAGE / FAST FASH stamp overlay.
   *  Used for quick-posted items that haven't run through fig-verify —
   *  their verdict column carries a "FAIL" placeholder for the NOT NULL
   *  constraint, but they shouldn't display as failed verifications. */
  unverified?: boolean;
}

/**
 * Horizontal swipe-able carousel for post media. CSS scroll-snap drives the
 * paging; a tap on any slide bubbles onClick (for navigation to the result),
 * while horizontal drags pan the strip natively.
 */
export function PostCarousel({
  photos, alt, verdict, conf, soldAt, soldPrice, soldOn, onClick, unverified,
}: PostCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const startX = useRef<number | null>(null);
  const moved = useRef(false);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== active) setActive(idx);
  };

  const goTo = (i: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div style={{ position: "relative", borderBottom: "1px solid hsl(var(--ink))", background: "hsl(var(--paper-3))" }}>
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        onPointerDown={(e) => { startX.current = e.clientX; moved.current = false; }}
        onPointerMove={(e) => {
          if (startX.current != null && Math.abs(e.clientX - startX.current) > 6) moved.current = true;
        }}
        onPointerUp={() => {
          if (!moved.current) onClick?.();
          startX.current = null;
        }}
        style={{
          display: "flex",
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          width: "100%",
          // No fixed aspect ratio — each post's carousel sizes to its own
          // photos' natural heights. Feed reads as a mood-board mix of
          // portrait + landscape posts rather than uniform 4:3 plates.
          scrollbarWidth: "none",
          // Allow both axes so vertical drags pass through to the page scroll.
          // `pan-x pan-y` lets the browser pick direction from the initial
          // gesture: horizontal goes to this carousel's overflow-x, vertical
          // bubbles up so the user can scroll the feed by dragging anywhere
          // on the image. Plain `pan-x` was capturing vertical drags here
          // and blocking the page from scrolling.
          touchAction: "pan-x pan-y",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
          cursor: onClick ? "pointer" : "default",
        }}
      >
        {photos.map((src, i) => (
          <div
            key={i}
            style={{
              flex: "0 0 100%",
              scrollSnapAlign: "start",
              width: "100%",
              position: "relative",
            }}
          >
            <img
              src={src}
              alt={`${alt} ${i + 1}/${photos.length}`}
              draggable={false}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                userSelect: "none",
              }}
            />
          </div>
        ))}
      </div>

      {/* Stamp overlay (only on first slide visually, but placed over the
          frame). Suppressed entirely for unverified items so quick-posts
          don't render a misleading FAST FASH stamp based on their placeholder
          FAIL verdict. */}
      {!unverified && (
        <div style={{ position: "absolute", top: 10, right: 10, pointerEvents: "none" }}>
          <RubberStamp
            label={verdict === "PASS" ? "VINTAGE" : "FAST FASH"}
            sub={verdict === "PASS" ? "" : "REJECTED"}
            color={verdict === "PASS" ? "hsl(var(--rust))" : "hsl(var(--ink))"}
            size={70}
          />
        </div>
      )}

      {soldAt && (
        <div style={{
          position: "absolute", inset: 0,
          background: "hsla(0,0%,0%,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          pointerEvents: "none",
        }}>
          <RubberStamp
            label="SOLD"
            sub={soldPrice != null ? `$${Number(soldPrice).toFixed(0)}` : (soldOn ?? "")}
            color="hsl(var(--rust))"
            size={130}
          />
        </div>
      )}

      {photos.length > 1 && (
        <>
          {/* Dots */}
          <div style={{
            position: "absolute", bottom: 10, left: 0, right: 0,
            display: "flex", justifyContent: "center", gap: 6,
            pointerEvents: "auto",
          }}>
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); goTo(i); }}
                aria-label={`Go to image ${i + 1}`}
                style={{
                  width: 7, height: 7, borderRadius: "50%",
                  border: "1px solid hsl(var(--ink))",
                  background: i === active ? "hsl(var(--ink))" : "hsla(255,255,255,0.7)",
                  padding: 0, cursor: "pointer",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
